import type { Signal} from '@angular/core';
import { Injectable, computed, inject, signal } from '@angular/core';

import { AuthStore } from '../auth/auth.store';

/**
 * Contexto de tenant, expuesto como Signal en el root.
 *
 * AUTORIDAD = claim `tenantId` del JWT (no un header manipulable). El super-admin
 * PVP puede "actuar como" otro tenant vía `actAsTenant()` — override explícito y
 * auditable, NUNCA silencioso. El backend re-valida el tenant efectivo en cada
 * request contra los permisos del token.
 */
@Injectable({ providedIn: 'root' })
export class TenantContextService {
  private readonly auth = inject(AuthStore);

  /** Override de super-admin (impersonación de tenant). Null = tenant del claim. */
  private readonly _actingTenantId = signal<string | null>(null);

  /** Tenant efectivo: override de super-admin ?? tenant del claim JWT. */
  readonly tenantId: Signal<string | null> = computed(
    () => this._actingTenantId() ?? this.auth.tenantId(),
  );

  /** Tenant "propio" según el claim (para mostrar de dónde venís realmente). */
  readonly homeTenantId = computed(() => this.auth.tenantId());

  /** True si estás viendo un tenant distinto al tuyo (banner de impersonación). */
  readonly isImpersonating = computed(
    () => this._actingTenantId() !== null && this._actingTenantId() !== this.auth.tenantId(),
  );

  readonly canSwitchTenant = computed(() => this.auth.isSuperAdmin());

  /**
   * Cambia el tenant activo (sólo super-admin PVP). El cambio dispara re-fetch
   * en las features que dependan de `tenantId` (via effect/computed).
   */
  actAsTenant(tenantId: string): void {
    if (!this.auth.isSuperAdmin()) {
      throw new Error('[TenantContext] cambio de tenant no autorizado para este usuario.');
    }
    this._actingTenantId.set(tenantId);
  }

  /** Vuelve al tenant propio del claim. */
  resetTenant(): void {
    this._actingTenantId.set(null);
  }
}
